import { useContext } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Navigate, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import BaseButton from "../../../components/ui/BaseButton";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import CreateEditClientForm from "../../Client/CreateEditClient/CreateEditClientForm";
import { LayoutContext } from "../../../context/LayoutContext";
import useAuth from "../../../hooks/useAuth";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { UserType } from "../../../utils/enum";
import { createOwnEntity } from "../../../services/api/client";
import { showToast } from "../../../store/toast/toastSlice";
import { buildClientFormData } from "../../../utils/helpers";

const websiteTest = (value) => {
  if (!value) return false;
  const formatted = value.startsWith("http") ? value : `https://${value}`;
  try {
    const url = new URL(formatted);
    return /^(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/.test(url.hostname);
  } catch {
    return false;
  }
};

// The entity fields a customer sees: the servicing company's form minus branch,
// teams, billing terms and policies, which Tech Domain assigns afterwards.
export const customerEntityValidationSchema = Yup.object({
  clientName: Yup.string()
    .trim()
    .required("Entity Name is required")
    .max(255, "Entity Name must be at most 255 characters"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  phone: Yup.string().required("Phone is required"),
  manager: Yup.string().trim().required("Contact Person is required"),
  registrationNumber: Yup.string()
    .trim()
    .required("Registration Number is required")
    .max(50, "Registration Number must be at most 50 characters"),
  registrationCertificate: Yup.mixed().required("Registration Certificate is required"),
  address: Yup.string()
    .required("Address is required")
    .max(500, "Address must be at most 500 characters"),
  city: Yup.string().required("City is required"),
  state: Yup.string().notRequired(),
  zipCode: Yup.string().required("Zip Code is required"),
  country: Yup.string().required("Country is required"),
  website: Yup.string()
    .notRequired()
    .test("is-valid-website", "Enter a valid website ", (value) => !value || websiteTest(value)),
  accountsPerson: Yup.string().required("Accounts Person is required"),
  accountsEmail: Yup.string().required("Email is required").email("Invalid email format"),
  accountsPhone: Yup.string().notRequired(),
  taxRegistrationNumber: Yup.string().notRequired(),
});

const initialValues = {
  clientName: "",
  manager: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  phone: "",
  email: "",
  teamEmail: "",
  website: "",
  policies: "",
  clientLogo: null,
  registrationCertificate: null,
  latitude: "",
  longitude: "",
  branchId: null,
  teamId: "",
  secondaryTeamId: "",
  accountsPerson: "",
  accountsEmail: "",
  accountsPhone: "",
  paymentTerms: null,
  taxType: null,
  taxPercentage: null,
  taxRegistrationNumber: "",
  registrationNumber: "",
};

const ENTITIES_PATH = "/customerSettings/entities";

// The customer-side "Create Entity" page: the same entity form the servicing company
// fills in, posted to the customer's own endpoint and returning to the Entities tab.
const CreateEntity = () => {
  const { isBannerVisible } = useContext(LayoutContext);
  const { auth, authRole } = useAuth();
  const { userData, isUserLoading } = useCurrentUser();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { isPending, mutateAsync } = useMutation({ mutationFn: createOwnEntity });

  const formik = useFormik({
    initialValues,
    validationSchema: customerEntityValidationSchema,
    onSubmit: async (values) => {
      try {
        await mutateAsync(buildClientFormData(values));
        dispatch(showToast({ type: "success", message: "Entity created successfully" }));
        await queryClient.invalidateQueries({ queryKey: ["customerEntities"] });
        navigate(ENTITIES_PATH);
      } catch (error) {
        dispatch(
          showToast({
            type: "error",
            message: error?.response?.data?.message || "Failed to create entity",
          }),
        );
      }
    },
  });

  // Only the account that owns the customer adds entities; the API enforces the
  // same rule, this just keeps everyone else off the page.
  const isOwner = auth?.user?.isCustomerOwner ?? userData?.isCustomerOwner;
  if (authRole !== UserType.CUSTOMER) return <Navigate to="/unauthorized" replace />;
  if (isOwner === undefined && isUserLoading) return null;
  if (!isOwner) return <Navigate to={ENTITIES_PATH} replace />;

  return (
    <div
      className={`bg-[#F6F6F6] h-full relative ${isBannerVisible ? "top-[107px]" : "top-[56px]"}`}
    >
      <div className="border-b border-zinc-300 bg-white w-full sticky top-0 z-10">
        <BaseWrapper>
          <div className="grid grid-cols-7 py-[7px] items-center">
            <div className="sm:col-span-3 col-span-7">
              <h3 className="font-light">Add Entity</h3>
            </div>
            <div className="sm:col-span-4 col-span-7 flex items-center justify-end gap-3">
              <BaseButton
                variant="contained"
                color="primary"
                className="py-[5px]"
                disabled={isPending}
                onClick={() => navigate(ENTITIES_PATH)}
              >
                Cancel
              </BaseButton>
              <BaseButton
                loaderSize="24px"
                isLoading={isPending}
                disabled={isPending || !formik.isValid || !formik.dirty}
                type="submit"
                variant="contained"
                color="primary"
                className="py-[5px]"
                onClick={formik.handleSubmit}
              >
                Save
              </BaseButton>
            </div>
          </div>
        </BaseWrapper>
      </div>
      <CreateEditClientForm
        formik={formik}
        isEditable
        isEditMode={false}
        isParentClient={false}
        recordLabel="Entity"
      />
    </div>
  );
};

export default CreateEntity;
